import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@web/core/permissions/permissions';
import { NewContactDialog } from '@web/modules/contacts/create/new-contact-dialog';
import { ContactDrawer } from '@web/modules/contacts/detail/contact-drawer';
import { ContactsPage } from '@web/modules/contacts/list/contacts-page';
import { parseContactListSearch } from '@web/modules/contacts/list/contact-search';
import { parseImportSearch, withoutImport } from '@web/modules/imports/import-search';
import { ImportDrawer } from '@web/modules/imports/wizard/import-drawer';
import { toast } from '@web/shared/ui/toast';

export const Route = createFileRoute('/_app/contacts/')({
  // `?import=contacts` opens the import drawer over the list (spec 033); `?contactId=` a
  // person's drawer (spec 064).
  validateSearch: (search: Record<string, unknown>) => ({
    ...parseContactListSearch(search),
    ...parseImportSearch(search, 'contacts'),
    ...parseContactDrawerSearch(search),
  }),
  component: ContactsRoute,
});

/** `?contactId=<uuid>`: the person whose drawer is open; anything else is dropped. */
function parseContactDrawerSearch(search: Record<string, unknown>): { contactId?: string } {
  const value = search['contactId'];
  return typeof value === 'string' && UUID.test(value) ? { contactId: value } : {};
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The people of the CRM (spec 033): the list, "Novo contato", the import and a person's
 * drawer (spec 064).
 */
function ContactsRoute() {
  const { t } = useTranslation();
  const routeSearch = Route.useSearch();
  const { contactId, ...listSearch } = routeSearch;
  const search = withoutImport(listSearch);
  const navigate = Route.useNavigate();
  const canCreate = useCan('core.contact.create');
  const canImport = useCan('core.contact.import');
  const [creating, setCreating] = useState(false);
  return (
    <>
      <ContactsPage
        search={search}
        onSearchChange={(next, options) =>
          void navigate({ search: next, replace: options?.replace })
        }
        onOpenContact={(id) => void navigate({ search: { ...search, contactId: id } })}
        onNewContact={canCreate ? () => setCreating(true) : undefined}
        onImport={
          canImport ? () => void navigate({ search: { ...search, import: 'contacts' } }) : undefined
        }
      />
      <ContactDrawer contactId={contactId ?? null} onClose={() => void navigate({ search })} />
      <ImportDrawer
        entity="contacts"
        open={routeSearch.import === 'contacts'}
        onClose={() => void navigate({ search })}
      />
      <NewContactDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(contact) => {
          setCreating(false);
          toast.success({ title: t('contacts.create.created', { name: contact.name }) });
        }}
      />
    </>
  );
}
